import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppRoleAssignment } from "src/App/api/APIClient";

export default function CreateUser({
    roles,
    validDomain,
    onAdd,
}: {
    roles: AppRoleAssignment[];
    validDomain?: string;
    onAdd: (payload: {
        firstName: string;
        username: string;
        roleId: string;
        allowAccess: boolean;
    }) => void;
}) {
    const { t } = useTranslation("project");

    const [firstName, setFirstName] = useState("");
    const [username, setUsername] = useState("");
    const [selectedRoleId, setSelectedRoleId] = useState("");
    const [grantInitialPermissions, setGrantInitialPermissions] = useState(true);

    const isFormValid = firstName.trim() !== "" && username.trim() !== "" && !!selectedRoleId;

    const handleAdd = () => {
        if (!isFormValid) return;
        onAdd({
            firstName,
            username,
            roleId: selectedRoleId,
            allowAccess: grantInitialPermissions,
        });
        setFirstName("");
        setUsername("");
        setSelectedRoleId("");
        setGrantInitialPermissions(true);
    };

    return (
        <Box role="tabpanel" id="user-tabpanel-0" aria-labelledby="user-tab-0" sx={{ mt: 2 }}>
            <Box>
                <Grid container spacing={2} padding={1}>
                    <Grid item xs={12} md={5}>
                        <TextField
                            id="firstName"
                            label={t("userController.create.nameLabel")}
                            placeholder={t("userController.create.namePlaceholder")}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            fullWidth
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <TextField
                            id="username"
                            label={t("userController.create.usernameLabel")}
                            placeholder={t("userController.create.usernamePlaceholder")}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            fullWidth
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">{validDomain}</InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} padding={1}>
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth>
                            <InputLabel id="role-create-label">
                                {t("userController.create.roleLabel")}
                            </InputLabel>
                            <Select
                                labelId="role-create-label"
                                id="role-create"
                                value={selectedRoleId}
                                label={t("userController.create.roleLabel")}
                                required
                                onChange={(e) => setSelectedRoleId(e.target.value as string)}
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role.id} value={role.principalId}>
                                        {role.principalDisplayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={grantInitialPermissions}
                                    onChange={(e) => setGrantInitialPermissions(e.target.checked)}
                                />
                            }
                            label={t("userController.create.enableInitialPerms")}
                        />
                    </Grid>
                </Grid>

                <Button
                    variant="contained"
                    onClick={handleAdd}
                    disabled={!isFormValid}
                    fullWidth
                    color={isFormValid ? "primary" : "inherit"}
                >
                    {t("userController.create.addButton")}
                </Button>
            </Box>
        </Box>
    );
}
